import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch} from 'react-redux';
import {UPDATE_SELECTED_PENDING_OFFER_CANDIDATE} from 'src/utils/crm';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  ButtonTextMedium,
  SubTitleExtraSmall,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {SimpleButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import {ApprovalCard} from 'src/components/crm/dashboard/approvals/approvals-card/approvals-card';
import {ApproveOfferForm} from 'src/components/crm/lib/tasks/approve-offer/approve-offer-form/approve-offer-form';
import {ApproveJobForm} from 'src/components/crm/lib/tasks/approve-job/approve-job-form/approve-job-form';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';

import css from './pending-approvals-card.css';
import {type MenuOption} from '@spaced-out/ui-design-system';


export const getTabs = (
  t: (key: string, defaultValue: string) => string,
): Array<{
  label?: string;
  tabId: string;
}> => [
  {
    label: t('Pending', 'Pending'),
    tabId: 'Pending',
  },
  {
    label: t('Approved', 'Approved'),
    tabId: 'Approved',
  },
  {
    label: t('Rejected', 'Rejected'),
    tabId: 'Rejected',
  },
];

const getApprovalType = (approval: Approval) => {
  if (approval.offerLetterId) {
    return 'OFFER_LETTER';
  }
  return 'JOB';
};

type Approval = {
  id: string;
  createdOn: number;
  jobTitle: string;
  offerLetterId?: string;
  candidateName?: string;
  stepId?: string;
  candidateId?: string;
  status: string;
  jobId: string;
  stageId?: string;
  approverId?: string;
  updatedOn?: number;
  updatedBy?: string;
};

type TabItem = {
  label: string;
  tabId: string;
};

type Props = {
  pendingApprovals?: Array<Approval>;
  rejectedApprovals?: Array<Approval>;
  approvedApprovals?: Array<Approval>;
  onSubmit?: () => void;
  isApprovalsLoading: boolean;
};

const PendingApprovals = ({
  pendingApprovals = [],
  rejectedApprovals = [],
  approvedApprovals = [],
  onSubmit,
  isApprovalsLoading,
}: Props) => {
  const {t, makeKey, language} = useLabelTranslation();
  const tabs = getTabs(t);
  const [selected, setSelected] = React.useState(tabs[0]);
  const [jobId, setJobId] = React.useState('');
  const [approvalId, setApprovalId] = React.useState('');
  const [isOfferFormOpen, setOfferFormOpen] = React.useState(false);
  const [isJobApprovalFormOpen, setIsJobApprovalFormOpen] =
    React.useState(false);
  const [isApproved, setIsApproved] = React.useState(false);
  const [isRejected, setIsRejected] = React.useState(false);
  const [isPendingApprovalPanelOpen, setIsPendingApprovalPanelOpen] =
    React.useState(false);
  const [panelTabSelected, setPanelTabSelected] = React.useState(tabs[0]);
  const [approvalTypeFilter, setApprovalTypeFilter] = React.useState('All');

  const dispatch = useDispatch();

  const approvalTypeOptions = React.useMemo(
    () => [
      {key: 'All', label: t('All', 'All')},
      {
        key: 'Offer Approval',
        label: t('Offer_Approval', 'Offer Approval'),
      },
      {key: 'Job Approval', label: t('Job_Approval', 'Job Approval')},
    ],
    [language],
  );

  const handleClick = (approval: Approval) => {
    const {
      stageId = '',
      candidateId = '',
      candidateName: name = '',
      jobId,
      id: approvalId,
      status,
    } = approval;

    const approvalType = getApprovalType(approval);
    setIsRejected(status === 'REJECTED');
    setIsApproved(status === 'APPROVED');
    if (approvalType === 'JOB') {
      setIsJobApprovalFormOpen(true);
      setJobId(jobId);
      setApprovalId(approvalId);
    } else {
      dispatch({
        //@ts-expect-error
        type: UPDATE_SELECTED_PENDING_OFFER_CANDIDATE,
        payload: {
          //@ts-expect-error
          stageId,
          candidateId,
          name,
        },
      });
      setOfferFormOpen(true);
      setJobId(jobId);
    }
  };

  const filterByApprovalType = (items: Array<Approval>) => {
    if (approvalTypeFilter === 'All') {
      return items;
    }
    return items.filter((item) => {
      const type = getApprovalType(item);
      if (approvalTypeFilter === 'Offer Approval') {
        return type === 'OFFER_LETTER';
      }
      if (approvalTypeFilter === 'Job Approval') {
        return type === 'JOB';
      }
      return true;
    });
  };

  const pendingItems = React.useMemo(() => {
    const sorted = [...(pendingApprovals || [])].sort(
      (a, b) => b.createdOn - a.createdOn,
    );
    return sorted;
  }, [pendingApprovals]);

  const approvedItems = React.useMemo(() => {
    const sorted = [...approvedApprovals].sort(
      (a, b) => b.createdOn - a.createdOn,
    );
    return sorted;
  }, [approvedApprovals]);

  const rejectedItems = React.useMemo(() => {
    const sorted = [...rejectedApprovals].sort(
      (a, b) => b.createdOn - a.createdOn,
    );
    return sorted;
  }, [rejectedApprovals]);

  const filteredPendingItems = React.useMemo(() => {
    const filtered = filterByApprovalType([...(pendingApprovals || [])]);
    return filtered.sort((a, b) => b.createdOn - a.createdOn);
  }, [pendingApprovals, approvalTypeFilter]);

  const filteredApprovedItems = React.useMemo(() => {
    const filtered = filterByApprovalType([...approvedApprovals]);
    return filtered.sort((a, b) => b.createdOn - a.createdOn);
  }, [approvedApprovals, approvalTypeFilter]);

  const filteredRejectedItems = React.useMemo(() => {
    const filtered = filterByApprovalType([...rejectedApprovals]);
    return filtered.sort((a, b) => b.createdOn - a.createdOn);
  }, [rejectedApprovals, approvalTypeFilter]);

  const handleApprovalTypeSelect = (option: MenuOption) => {
    setApprovalTypeFilter(option.key);
  };

  const handleCloseJobApproval = () => {
    setIsJobApprovalFormOpen(false);
    setIsApproved(false);
    setIsRejected(false);
  };

  const getTabsWithCount = (useFilteredApprovals = false) => {
    return tabs.map((tab) => {
      let count = 0;
      switch (tab.tabId) {
        case 'Pending':
          count = useFilteredApprovals
            ? filteredPendingItems.length
            : pendingItems.length;
          break;
        case 'Approved':
          count = useFilteredApprovals
            ? filteredApprovedItems.length
            : approvedItems.length;
          break;
        case 'Rejected':
          count = useFilteredApprovals
            ? filteredRejectedItems.length
            : rejectedItems.length;
          break;
        default:
          count = 0;
      }
      return {
        ...tab,
        label: `${tab.label || ''}${count ? ` (${count})` : ''}`,
      };
    });
  };
  const tabsWithCount = getTabsWithCount(false);
  const panelTabsWithCount = getTabsWithCount(true);
  const totalCount =
    (Array.isArray(pendingItems) ? pendingItems.length : 0) +
    (Array.isArray(approvedItems) ? approvedItems.length : 0) +
    (Array.isArray(rejectedItems) ? rejectedItems.length : 0);

  const renderApprovalItems = (
    items: Array<Approval> | Array<never>,
    emptyTitle: any,
  ) => {
    return items.length ? (
      items.map((approval, index) => (
        <ApprovalCard
          key={
            Number(approval.offerLetterId) > 0
              ? `offerLetter_${approval.id}`
              : approval.id
          }
          pendingapproval={approval}
          handleClick={() => handleClick(approval)}
        />
      ))
    ) : (
      <EmptyState
        title={emptyTitle}
        description={`${t(
          'All_clear_for_now_Keep_an_eye_out',
          'All clear for now. Keep an eye out',
        )}!`}
        imageVariant="file"
      />
    );
  };

  const getItemsAndTitle = (tabId: string, useFilteredApprovals = false) => {
    // @ts-expect-error - TS7034 - Variable 'items' implicitly has type 'any[]' in some locations where its type cannot be determined.
    let items;
    let emptyTitle;

    switch (tabId) {
      case 'Pending':
        items = useFilteredApprovals ? filteredPendingItems : pendingItems;
        emptyTitle = t('No_pending_approvals', 'No pending approvals');
        break;
      case 'Approved':
        items = useFilteredApprovals ? filteredApprovedItems : approvedItems;
        emptyTitle = t('No_approved_items_yet', 'No approved items yet');
        break;
      case 'Rejected':
        items = useFilteredApprovals ? filteredRejectedItems : rejectedItems;
        emptyTitle = t(
          'No_rejected_approvals_yet',
          'No rejected approvals yet',
        );
        break;
      default:
        items = [];
        emptyTitle = t('No_items', 'No items');
    }

    // @ts-expect-error - TS7005 - Variable 'items' implicitly has an 'any[]' type.
    return {items, emptyTitle};
  };

  const renderContent = (
    selectedTab: {
      label?: string;
      tabId: string;
    },
    useFiltered = false,
  ) => {
    const {items, emptyTitle} = getItemsAndTitle(
      selectedTab.tabId,
      useFiltered,
    );
    return renderApprovalItems(items, emptyTitle);
  };

  return (
    <>
      {isOfferFormOpen && (
        <ApproveOfferForm
          isOpen={isOfferFormOpen}
          handleClose={() => {
            setOfferFormOpen(false);
          }}
          handleMoveBack={() => {
            setOfferFormOpen(false);
          }}
          currentJobId={jobId}
          isRejected={isRejected}
          isApproved={isApproved}
          onSubmit={onSubmit}
        />
      )}
      {isJobApprovalFormOpen && (
        <ApproveJobForm
          isOpen={isJobApprovalFormOpen}
          handleClose={handleCloseJobApproval}
          handleMoveBack={handleCloseJobApproval}
          currentJobId={jobId}
          currentApprovalId={approvalId}
          isRejected={isRejected}
          isApproved={isApproved}
          onSubmit={onSubmit}
        />
      )}
      <Card classNames={{wrapper: css.approvalCard}}>
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="thumbs-up"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Approvals', 'Approvals')}
            {pendingItems.length > 0 ? (
              <Badge text={String(pendingItems.length)} fill="blue" />
            ) : null}
          </CardTitle>
          <CardActions>
            {totalCount > 0 && (
              <Button
                children={t('View_All', 'View All')}
                type="ghost"
                size="small"
                onClick={() => {
                  setPanelTabSelected(selected);
                  setIsPendingApprovalPanelOpen(true);
                }}
              />
            )}
          </CardActions>
        </CardHeader>
        {isApprovalsLoading ? (
          <CircularLoader className={css.loader} />
        ) : (
          <>
            <div className={css.tabSlot}>
              <TabList
                onSelect={(tab) => {
                  setSelected(tab);
                }}
                selectedTab={{
                  label: selected.label,
                  tabId: selected.tabId,
                }}
                size="medium"
                menuWidth="100%"
              >
                {tabsWithCount.map(({label, tabId}) => (
                  <Tab key={tabId} label={label} tabId={tabId} />
                ))}
              </TabList>
            </div>

            <CardContent className={css.cardContent}>
              {renderContent(selected, false)}
            </CardContent>
          </>
        )}
      </Card>

      <Panel
        anchor="right"
        initialFocus={1}
        onClose={() => setIsPendingApprovalPanelOpen(false)}
        size="large"
        tapOutsideToClose
        isOpen={isPendingApprovalPanelOpen}
        hideBackdrop={false}
      >
        <PanelHeader
          onCloseButtonClick={() => setIsPendingApprovalPanelOpen(false)}
          size="medium"
        >
          {t('Approvals', 'Approvals')}
        </PanelHeader>
        <div className={css.tabSlot}>
          <TabList
            onSelect={(tab) => {
              setPanelTabSelected(tab);
            }}
            selectedTab={{
              label: panelTabSelected.label,
              tabId: panelTabSelected.tabId,
            }}
            size="medium"
            menuWidth="100%"
          >
            {panelTabsWithCount.map(({label, tabId}) => (
              <Tab key={tabId} label={label} tabId={tabId} />
            ))}
          </TabList>
        </div>

        <PanelBody className={css.panelBody}>
          <SimpleButtonDropdown
            size="small"
            type="ghost"
            children={t(makeKey(approvalTypeFilter), approvalTypeFilter)}
            anchorPosition="bottom-start"
            onOptionSelect={handleApprovalTypeSelect}
            options={approvalTypeOptions}
            selectedKeys={[approvalTypeFilter]}
            // @ts-expect-error - TS2769 - No overload matches this call.
            selectedOption={approvalTypeFilter}
          />
          {renderContent(panelTabSelected, true)}
        </PanelBody>
      </Panel>
    </>
  );
};

export default PendingApprovals;
